import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useBooking } from '../../../hooks/useBookings';
import { usePaymentBooking } from '../../../hooks/usePaymentBooking';
import { usePaymentStatus } from '../../../hooks/usePaymentStatus';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';

import { paymentService } from '../../../services/paymentService';
import { localStorageHelper } from '../../../utils/localStorageHelper';

export const useBookingDetailsPage = () => {
  const { t } = useTranslation();
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user } = useAuthContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [justPaid, setJustPaid] = useState(false);
  const isInIframe = useMemo(() => window.self !== window.top, []);

  const { 
    bookingDetail, 
    detailLoading, 
    detailError, 
    fetchBookingDetail, 
    updateBooking, 
    updateLoading,
    cancelBooking,
    cancelLoading
  } = useBooking();

  const { 
    status: paymentStatus, 
    startPolling: startPaymentCheck, 
    stopPolling 
  } = usePaymentStatus();

  const { 
    initiatePayment, 
    loading: loadingPayment, 
    paymentUrl, 
    clearPaymentUrl 
  } = usePaymentBooking();

  const shouldCheckPayment = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('isPayment') === '1';
  }, [location.search]);

  const handleInitiatePayment = useCallback(() => {
    if (uid) initiatePayment(uid);
  }, [uid, initiatePayment]);

  const handleAction = useCallback(async (actionType: string) => {
    // Determine if the logged-in user is the professional of this booking.
    // A professional can also book sessions as a patient, so we check by uid, not account type.
    const isBookingProfessional = bookingDetail?.professional_uid
      ? user?.uid === bookingDetail.professional_uid
      : user?.type_account === 'professional';

    switch (actionType) {
      case 'WAITING_PAYMENT':
        // Allow payment if the user is the patient of this booking (not the professional side)
        if (!isBookingProfessional) {
          handleInitiatePayment();
        }
        break;
      case 'COMPLETED':
        navigate(`/my-consultation/${bookingDetail?.consultation?.uid}/details-consultation`);
        break;
      case 'CONFIRMED':
        if (user?.type_account === 'professional' && bookingDetail?.uid) {
          navigate(`/reservations/${bookingDetail.uid}/create-report`);
        }
        break;
      case 'PROCESSING':
      case 'PENDING_PROVIDER':
        if (uid) {
          setIsStatusUpdating(true);
          try {
            await paymentService.getPaymentStatus(uid);
            await fetchBookingDetail(uid);
            showNotification(t('appAdmin.BookingDetailsPage.statusUpdated'), 'success');
          } catch (error: any) {
            showNotification(error.message || t('appAdmin.BookingDetailsPage.statusUpdateError'), 'error');
          } finally {
            setIsStatusUpdating(false);
          }
        }
        break;
      default:
        break;
    }
  }, [user, bookingDetail, uid, navigate, fetchBookingDetail, handleInitiatePayment, showNotification, t]);

  const handleSecondaryAction = useCallback(async (actionType: string) => {
    if (actionType === 'WAITING_PAYMENT') {
        if (uid) {
            const result = await cancelBooking(uid);
            if (result) {
                showNotification(t('appAdmin.BookingDetailsPage.bookingCancelled', 'Reserva cancelada'), 'success');
            }
        }
    }
  }, [uid, cancelBooking, showNotification, t]);

  useEffect(() => {
    if (uid) fetchBookingDetail(uid);
    else navigate('/');
  }, [uid, fetchBookingDetail, navigate]);

  useEffect(() => {
    if (shouldCheckPayment && uid) {
      if (isInIframe) {
        window.parent.postMessage({ type: 'PAYMENT_COMPLETE', url: window.location.href }, '*');
      } else {
        setIsModalOpen(true);
        startPaymentCheck(uid);
      }
    }
  }, [shouldCheckPayment, uid, startPaymentCheck, isInIframe]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    stopPolling();
    if (uid) {
      fetchBookingDetail(uid);
      navigate(`/professionals/booking-details/${uid}`, { 
        replace: true,
        state: justPaid ? { showSuccessToast: true } : undefined
      });
    }
  }, [uid, stopPolling, fetchBookingDetail, navigate, justPaid]);

  const handleRetryPayment = useCallback(() => {
    setIsModalOpen(false);
    stopPolling();
    handleInitiatePayment();
  }, [setIsModalOpen, stopPolling, handleInitiatePayment]);

  if (detailError) {
    localStorageHelper.setItem('redirectAfterLogin', location.pathname + location.search);
  }

  const handlePaymentIframeClose = useCallback(() => {
    clearPaymentUrl();
    if (uid) {
      setIsModalOpen(true);
      startPaymentCheck(uid);
    }
  }, [clearPaymentUrl, uid, startPaymentCheck]);

  useEffect(() => {
    if (paymentStatus) {
      const normalizedStatus = paymentStatus.toLowerCase();
      if (['captured', 'approved', 'authorized', 'paid'].includes(normalizedStatus)) {
        setJustPaid(true);
      }
    }
  }, [paymentStatus]);

  return {
    t,
    uid,
    bookingDetail,
    loading: detailLoading,
    error: detailError,
    isActionLoading: loadingPayment || isStatusUpdating,
    isSecondaryActionLoading: cancelLoading,
    updateLoading,
    paymentStatus,
    isModalOpen,
    paymentUrl,
    handleAction,
    handleSecondaryAction,
    handleCloseModal,
    handleRetryPayment,
    updateBooking,
    handlePaymentIframeClose,
    isInIframe,
    shouldCheckPayment,
    userType: user?.type_account,
    justPaid
  };
};